import type { Random } from "../core/random.js";
import type { MatchPerformance } from "./player.js";
import type {
  AttributeKey,
  ClubStyle,
  Objective,
  ObjectiveKind,
  Position,
  RelationKey,
  Relations,
  Sender,
} from "./model.js";
import { AGENT_NAME, PAPER_NAME, starName } from "./names.js";
import { pick, rngFor } from "./rng.js";
import type { Draft } from "./phone.js";

/**
 * El movil que se CONTESTA (y que sirve para algo).
 *
 * Pensado como el de GTA: la gente te escribe, tu contestas, y lo que dices
 * tiene consecuencias. No hay un medidor de "karma" escondido que no se nota
 * nunca; hay tres relaciones que el juego NOTA:
 *
 * - El MISTER (confianza): cuanto te trabaja. Con el de tu lado creces mas
 *   rapido; con el en contra, mas despacio (`growthFactor`).
 * - El VESTUARIO (quimica): tus compañeros de la IA juegan un punto mejor, o
 *   peor, contigo en el campo (`squadSkillOffset`, en `matchSetup.ts`).
 * - La AFICION (fama): los clubes te miran mas, o menos, al final de la
 *   temporada (`offerBoost`, en las ofertas).
 *
 * Y dos cosas que cambian un partido concreto:
 * - El ENCARGO del mister: un objetivo medible para cada partido. Cumplirlo
 *   da confianza y un empujon a un atributo; fallarlo, lo contrario.
 * - PICAR al rival (en la prensa o por privado): sale mas motivado. Si le
 *   ganas, la aficion te adora; si pierdes, te lo recuerdan.
 *
 * Todo es puro: nada de DOM ni de reloj. Lo aleatorio sale de la semilla.
 */

export const RELATION_START = 50;

export function relationsOf(c: { relations?: Relations }): Relations {
  return c.relations ?? { coach: RELATION_START, squad: RELATION_START, fans: RELATION_START };
}

const clamp = (v: number): number => Math.max(0, Math.min(100, Math.round(v)));

export function applyRelations(base: Relations, effect: Partial<Relations>): Relations {
  return {
    coach: clamp(base.coach + (effect.coach ?? 0)),
    squad: clamp(base.squad + (effect.squad ?? 0)),
    fans: clamp(base.fans + (effect.fans ?? 0)),
  };
}

/** Confianza del mister → cuanto creces por partido: de 0,9 (0) a 1,1 (100). */
export function growthFactor(r: Relations): number {
  return 0.9 + r.coach / 500;
}

/** Quimica del vestuario → nivel de tus compañeros de la IA: de -0,1 a +0,1. */
export function squadSkillOffset(r: Relations): number {
  return (r.squad - RELATION_START) / 500;
}

/** Fama → nivel con el que te miran los clubes al ofertar: de -5 a +5. */
export function offerBoost(r: Relations): number {
  return (r.fans - RELATION_START) / 10;
}

/** Un rival picado sale asi de mas afinado. */
export const PROVOKED_SKILL = 0.08;

// ------------------------------------------------------------------ preguntas

export interface ReplyOption {
  /** Lo que contestas (clave de `t()`). */
  say: string;
  /** Lo que te contestan (clave de `t()`). Mismos parametros que la pregunta. */
  back: string;
  effect: Partial<Relations>;
  /** Picas al rival de la jornada. */
  provoke?: boolean;
}

export interface Question {
  from: Sender;
  options: readonly ReplyOption[];
}

/**
 * Cada pregunta, con sus respuestas. El mensaje que pregunta lleva `ask` con
 * el id; al contestar se añaden dos burbujas: la tuya y la suya.
 */
export const QUESTIONS: Readonly<Record<string, Question>> = {
  brief: {
    from: "coach",
    options: [
      { say: "Entendido, míster.", back: "Así me gusta. Obedecer también es un talento.", effect: { coach: 4 } },
      {
        say: "¿Y si juego a lo mío?",
        back: "Juega a lo tuyo y luego se lo explicas al banquillo. Desde el banquillo.",
        effect: { coach: -5, fans: 3 },
      },
      {
        say: "Lo que haga falta por el equipo.",
        back: "Eso díselo al utillero, que está sensible. Pero bien dicho.",
        effect: { coach: 2, squad: 3 },
      },
    ],
  },
  interview: {
    from: "press",
    options: [
      {
        say: "Máximo respeto al rival.",
        back: "{paper}: «Titular: \"Máximo respeto\". Nos hemos dormido escribiéndolo.»",
        effect: { fans: 1 },
      },
      {
        say: "Les vamos a pintar la cara.",
        back: "{paper}: «Portada. El {rival} ya la ha colgado en su vestuario.»",
        effect: { fans: 5, coach: -3 },
        provoke: true,
      },
      {
        say: "Hablaré en el campo.",
        back: "{paper}: «{name} no habla. Su agente dice que cobra por palabra.»",
        effect: { coach: 2 },
      },
    ],
  },
  rival: {
    from: "rival",
    options: [
      { say: "Tráete espinilleras.", back: "Me las traigo. De tu talla no las hacen.", effect: { fans: 2 }, provoke: true },
      { say: "Suerte, crack.", back: "Qué educado. Así no se vende un partido.", effect: { coach: 1 } },
    ],
  },
  dinner: {
    from: "squad",
    options: [
      { say: "Me apunto. El pan lo pago yo.", back: "Eso ha sonado a leyenda. Traigo el cántico.", effect: { squad: 6, coach: -2 } },
      { say: "Paso, el jueves toca descansar.", back: "¿Míster? ¿Eres tú? Devuélvele el móvil.", effect: { squad: -4, coach: 3 } },
    ],
  },
  ad: {
    from: "agent",
    options: [
      { say: "Hazlo. Quiero ser famoso.", back: "Hecho. Sales tres segundos. Dos de espaldas.", effect: { fans: 5, coach: -3 } },
      { say: "Primero el fútbol.", back: "Qué aburrido eres. Y qué rentable a largo plazo.", effect: { coach: 3 } },
    ],
  },
  loss: {
    from: "coach",
    options: [
      {
        say: "La culpa es mía.",
        back: "Por lo menos alguien lo dice. Mañana, doble sesión. Para ti, triple.",
        effect: { coach: 4, squad: 3 },
      },
      { say: "El árbitro no nos ha dejado.", back: "El árbitro no ha fallado ese control. Tú sí.", effect: { coach: -4 } },
      { say: "Pregúntale a la defensa.", back: "Se lo pregunto. Y de paso les enseño tu mensaje.", effect: { squad: -6, coach: -1 } },
    ],
  },
  mom: {
    from: "mom",
    options: [
      { say: "Sí, mamá.", back: "Mentira. Te mando un táper. Y otro para tus compañeros, que los veo flojos.", effect: { squad: 2 } },
      { say: "Mamá, es la cámara.", back: "La cámara no miente. Tu primo sí, pero la cámara no.", effect: {} },
    ],
  },
};

/** La pregunta de un mensaje, si la hay y existe. */
export function questionOf(ask: string | undefined): Question | null {
  return ask ? (QUESTIONS[ask] ?? null) : null;
}

// ------------------------------------------------------------------ encargos

/** El nombre corto de cada encargo, para la pantalla (clave de `t()`, con `{n}`). */
const OBJECTIVE_TEXT: Record<ObjectiveKind, string> = {
  score: "Marca {n} gol",
  shots: "Tira {n} veces",
  assist: "Da {n} asistencia",
  passes: "Acierta el {n} % de tus pases",
  recoveries: "Recupera {n} balones",
  cleanSheet: "Encaja {n} gol como mucho",
  win: "Gana el partido",
};

/** La clave del texto de un encargo ("Tira 3 veces"). Se traduce con `{ n: target }`. */
export function objectiveKey(o: Pick<Objective, "kind" | "target">): string {
  if (o.kind === "cleanSheet" && o.target === 0) return "Deja la portería a cero";
  return OBJECTIVE_TEXT[o.kind];
}

/** Cuantos pases hacen falta para que el porcentaje cuente. */
export const PASSES_MIN = 6;

/** Lo que se mejora al cumplirlo. */
export const OBJECTIVE_REWARD: Record<ObjectiveKind, AttributeKey> = {
  score: "shooting",
  shots: "shooting",
  assist: "passing",
  passes: "passing",
  recoveries: "stamina",
  cleanSheet: "stamina",
  win: "pace",
};

/** Cuanto sube ese atributo al cumplir (antes de la edad y el potencial). */
export const OBJECTIVE_BONUS = 0.35;

/**
 * El encargo de un partido, segun lo que haces en el campo y como juega el
 * rival. Contra un cerrojo se pide tirar; contra la presion, mover rapido;
 * contra el toque o la contra, robar.
 */
export function chooseObjective(
  random: Random,
  position: Position,
  rivalStyle: ClubStyle,
  tier: number,
): Omit<Objective, "season" | "matchday"> {
  const byStyle: Partial<Record<ClubStyle, ObjectiveKind>> = {
    cerrojo: "shots",
    presion: "passes",
    toque: "recoveries",
    contra: "recoveries",
    patadon: "recoveries",
  };
  const byPosition: Record<Position, readonly ObjectiveKind[]> = {
    DEL: ["score", "shots", "win"],
    MED: ["passes", "assist", "shots"],
    DEF: ["recoveries", "cleanSheet", "win"],
  };
  // La mitad de las veces manda el rival (si el encargo le pega a tu puesto).
  const styled = byStyle[rivalStyle];
  const kind = styled && byPosition[position].includes(styled) && random() < 0.5 ? styled : pick(random, byPosition[position]);
  return { kind, target: objectiveTarget(kind, tier, position) };
}

function objectiveTarget(kind: ObjectiveKind, tier: number, position: Position): number {
  switch (kind) {
    case "score":
    case "assist":
      return 1;
    case "shots":
      return (position === "DEL" ? 3 : 2) + (tier >= 2 ? 1 : 0);
    case "passes":
      return 80;
    case "recoveries":
      return 3 + tier;
    case "cleanSheet":
      // En el 3v3 y el 5v5 caen muchos goles: cero seria pedir un milagro.
      return tier <= 1 ? 1 : 0;
    case "win":
      return 1;
  }
}

/** Lo que llevas del encargo con estos numeros (para el marcador del partido). */
export function objectiveProgress(
  o: Pick<Objective, "kind" | "target">,
  p: Pick<MatchPerformance, "goals" | "shots" | "assists" | "passes" | "passesOk" | "recoveries" | "goalsFor" | "goalsAgainst">,
): { value: number; done: boolean } {
  switch (o.kind) {
    case "score":
      return { value: p.goals, done: p.goals >= o.target };
    case "shots":
      return { value: p.shots, done: p.shots >= o.target };
    case "assist":
      return { value: p.assists, done: p.assists >= o.target };
    case "passes": {
      const pct = p.passes > 0 ? Math.round((p.passesOk / p.passes) * 100) : 0;
      return { value: pct, done: p.passes >= PASSES_MIN && pct >= o.target };
    }
    case "recoveries":
      return { value: p.recoveries, done: p.recoveries >= o.target };
    case "cleanSheet":
      return { value: p.goalsAgainst, done: p.goalsAgainst <= o.target };
    case "win":
      return { value: p.goalsFor - p.goalsAgainst, done: p.goalsFor > p.goalsAgainst };
  }
}

// ------------------------------------------------------------------ vestuario

/**
 * Tus compañeros de cada club, siempre los mismos (salen de la semilla y del
 * club). Cuatro con papel fijo: el capitan, el portero, el veterano y el
 * canterano. Son los del grupo del movil.
 */
export interface Teammate {
  name: string;
  role: "captain" | "keeper" | "veteran" | "rookie";
}

export function squadOf(seed: number, clubId: string): Teammate[] {
  const random = rngFor(seed, "squad", clubId);
  const roles: Teammate["role"][] = ["captain", "keeper", "veteran", "rookie"];
  // Nombres de pila distintos: en el grupo se les llama por el nombre, y dos
  // "Kenji" en el mismo vestuario no se distinguen.
  const used = new Set<string>();
  const first = (name: string): string => name.split(" ")[0] ?? name;
  return roles.map((role) => {
    let name = "";
    for (let i = 0; i < 16 && (!name || used.has(first(name))); i++) {
      name = starName(Math.floor(random() * 997), Math.floor(random() * 991));
    }
    used.add(first(name));
    return { name, role };
  });
}


const msg = (from: Sender, key: string, params?: Record<string, string | number>, extra: Partial<Draft> = {}): Draft => ({
  from,
  key,
  ...(params ? { params } : {}),
  ...extra,
});

export interface TalkVoices {
  name: string;
  club: string;
  coach: string;
  squad: Teammate[];
}

/** Lo que se dice en el grupo del vestuario despues de un partido. */
export function squadBanter(random: Random, v: TalkVoices, a: {
  result: "win" | "draw" | "loss";
  goals: number;
  mvp: boolean;
  grade: number;
  absent: boolean;
  goalsAgainst: number;
}): Draft[] {
  const [captain, keeper, veteran, rookie] = v.squad as [Teammate, Teammate, Teammate, Teammate];
  const p = { name: v.name };
  const out: Draft[] = [];
  if (a.absent) {
    out.push(msg("squad", "¿Alguien sabe algo de {name}? El míster ha sacado a su sobrino.", p, { who: captain.name }));
    out.push(msg("squad", "El sobrino ha dado un pase. Uno.", p, { who: veteran.name }));
    return out;
  }
  if (a.goals >= 3) {
    out.push(msg("squad", "Hat-trick de {name}. Voy a enmarcar el balón. Y luego a venderlo.", p, { who: rookie.name }));
  } else if (a.mvp) {
    out.push(msg("squad", "MVP {name}. A partir de hoy las cañas las pagas tú.", p, { who: captain.name }));
  } else if (a.result === "win" && a.goals > 0) {
    out.push(msg("squad", "{name} marcando y yo aplaudiendo. Reparto de papeles perfecto.", p, { who: veteran.name }));
  } else if (a.result === "win") {
    out.push(msg("squad", "¡Victoria! Cena pagada por el que menos ha corrido: {lazy}.", { lazy: veteran.name }, { who: rookie.name }));
  } else if (a.result === "draw") {
    out.push(msg("squad", "Un empate. Mi madre dice que es como perder, pero con educación.", undefined, { who: rookie.name }));
  } else if (a.goalsAgainst >= 4) {
    out.push(msg("squad", "{n} goles. Me han metido {n} goles. Voy a cambiarme de número. Y de nombre.", { n: a.goalsAgainst }, { who: keeper.name }));
  } else if (a.grade < 5) {
    out.push(msg("squad", "Tranquilo, {name}. Lo importante es que el bocadillo estaba bueno.", p, { who: captain.name }));
  } else {
    out.push(msg("squad", "Nadie hable de esto. Nunca. Borrad el grupo.", undefined, { who: veteran.name }));
  }
  // A veces alguien contesta: es un grupo, no un tablon de anuncios.
  if (random() < 0.45) {
    const reply =
      a.result === "loss"
        ? pick(random, ["Yo ya lo he borrado.", "El míster ha dicho que mañana corremos. Gracias a todos."])
        : pick(random, ["Esto es un equipo, señores.", "Mañana nadie llega tarde. (Llegaré tarde.)", "Pido sitio en la foto."]);
    out.push(msg("squad", reply, undefined, { who: keeper.name }));
  }
  return out;
}

// ------------------------------------------------------------------ antes del partido

export interface PreMatch {
  rival: string;
  rivalId: string;
  rivalStyle: ClubStyle;
  rivalStar: string;
  /** Cuantos partidos de liga has jugado esta temporada (0 = el primero). */
  matchday: number;
  season: number;
  objective: Omit<Objective, "season" | "matchday">;
  /** El ultimo resultado, si lo hubo: una derrota trae pregunta del mister. */
  lastResult: "win" | "draw" | "loss" | null;
}

const COACH_TIP: Record<ClubStyle, string> = {
  cerrojo: "El {rival} aparca el autobús y le echa el freno de mano. Paciencia, que alguno se dormirá.",
  presion: "El {rival} presiona hasta al utillero. Toca rápido o te comen.",
  toque: "El {rival} se la pasa como si cobrara por pase. Quítasela y corre.",
  patadon: "El {rival} juega al pelotazo. Mira al cielo y reza.",
  contra: "El {rival} te deja el balón. Es una trampa. No te lo creas.",
};

/**
 * Lo que llega al movil antes de cada partido: el informe del rival (con la
 * pregunta del mister), el encargo, y a veces algo mas que contestar — la
 * prensa, el rival, el vestuario, tu agente o tu madre. Uno como mucho de
 * esos, para que el movil no sea un buzon de spam.
 */
export function preMatchTalk(random: Random, v: TalkVoices, m: PreMatch): Draft[] {
  const out: Draft[] = [];
  if (m.lastResult === "loss" && random() < 0.6) {
    out.push(msg("coach", "¿Me explicas qué ha pasado en el último partido?", undefined, { ask: "loss" }));
  }
  out.push(msg("coach", COACH_TIP[m.rivalStyle], { rival: m.rival, rivalId: m.rivalId }, { ask: "brief", card: "scout" }));
  out.push(
    msg("coach", "Tu encargo para el partido: {objective}. Cúmplelo y lo notarás. Fállalo y también.", {
      objective: `§${objectiveKey(m.objective)}`,
      n: m.objective.target,
    }, { card: "objective" }),
  );
  const extra = random();
  if (m.matchday === 3 && m.season % 2 === 1) {
    out.push(msg("squad", "Cena de equipo el jueves en el bar de siempre. ¿Te apuntas? 🍖", undefined, { who: v.squad[0]!.name, ask: "dinner" }));
  } else if (m.matchday === 5 && m.season % 3 === 2) {
    out.push(
      msg("agent", "Una marca de espinilleras quiere que salgas en un anuncio. Pagan en espinilleras. ¿Lo hacemos?", { agent: AGENT_NAME }, { ask: "ad" }),
    );
  } else if (extra < 0.3) {
    out.push(
      msg("press", "{paper}: «¿Unas palabras antes del partido contra el {rival}?»", { paper: PAPER_NAME, rival: m.rival, name: v.name }, { ask: "interview" }),
    );
  } else if (extra < 0.55) {
    out.push(msg("rival", "Soy {star}, del {rival}. El domingo te hago un túnel. Avisado quedas.", { star: m.rivalStar, rival: m.rival }, { who: m.rivalStar, ask: "rival" }));
  } else if (extra < 0.65) {
    out.push(msg("mom", "Hijo, ¿comes bien? En la tele te veo más delgado.", undefined, { ask: "mom" }));
  }
  return out;
}

/** Lo que te dice el mister del encargo, al acabar. */
export function objectiveVerdict(done: boolean, absent: boolean, o: Pick<Objective, "kind" | "target">): Draft {
  if (absent) return msg("coach", "Encargo: ni lo intentaste. No estabas.");
  // `st` dice como acabo, para pintar la tarjeta con su marca.
  const params = { objective: `§${objectiveKey(o)}`, n: o.target, st: done ? 1 : 0 };
  return done
    ? msg("coach", "Encargo cumplido: {objective}. Lo apunto. Y tú apunta que lo he dicho.", params, { card: "objective" })
    : msg("coach", "Encargo sin cumplir: {objective}. Tampoco era tan difícil. Bueno, sí. Pero no.", params, { card: "objective" });
}

/** La aficion, cuando le ganas (o no) a quien has picado. */
export function provokedOutcome(result: "win" | "draw" | "loss", rival: string): { draft: Draft; effect: Partial<Record<RelationKey, number>> } {
  // `who` es quien lo publica en la grada: el del bombo lo ve todo.
  const who = "@el_del_bombo";
  if (result === "win") {
    return { draft: msg("fans", "Le dijiste que le pintabas la cara al {rival} y se la pintaste. LEYENDA.", { rival, who }), effect: { fans: 6 } };
  }
  if (result === "loss") {
    return { draft: msg("fans", "Mucho hablar del {rival} y luego nada. Te lo vamos a recordar hasta en la boda.", { rival, who }), effect: { fans: -6 } };
  }
  return { draft: msg("fans", "Tanto picar al {rival} para un empate. Menos Twitter y más gol.", { rival, who }), effect: { fans: -2 } };
}
